import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SleepTimeline from './components/SleepTimeline';
import SleepTrendChart from './components/SleepTrendChart';
import SleepGoalProgress from './components/SleepGoalProgress';
import SleepLoggingControls from './components/SleepLoggingControls';
import SleepInsights from './components/SleepInsights';

const SleepAnalysis = () => {
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [sleepData, setSleepData] = useState([]);
  const [environmentData, setEnvironmentData] = useState({});

  // Mock sleep data
  useEffect(() => {
    const mockSleepData = [
      {
        date: '2025-10-05',
        bedtime: '22:30',
        wakeTime: '07:00',
        duration: '8.5h',
        quality: 85,
        consistency: 88,
        phases: [
          { type: 'light', duration: 25 },
          { type: 'deep', duration: 35 },
          { type: 'rem', duration: 30 },
          { type: 'awake', duration: 10 }
        ]
      },
      {
        date: '2025-10-06',
        bedtime: '23:00',
        wakeTime: '07:30',
        duration: '8.5h',
        quality: 78,
        consistency: 82,
        phases: [
          { type: 'light', duration: 30 },
          { type: 'deep', duration: 30 },
          { type: 'rem', duration: 25 },
          { type: 'awake', duration: 15 }
        ]
      },
      {
        date: '2025-10-07',
        bedtime: '22:15',
        wakeTime: '06:45',
        duration: '8.5h',
        quality: 92,
        consistency: 95,
        phases: [
          { type: 'light', duration: 20 },
          { type: 'deep', duration: 40 },
          { type: 'rem', duration: 35 },
          { type: 'awake', duration: 5 }
        ]
      },
      {
        date: '2025-10-08',
        bedtime: '22:45',
        wakeTime: '07:15',
        duration: '8.5h',
        quality: 80,
        consistency: 85,
        phases: [
          { type: 'light', duration: 28 },
          { type: 'deep', duration: 32 },
          { type: 'rem', duration: 28 },
          { type: 'awake', duration: 12 }
        ]
      },
      {
        date: '2025-10-09',
        bedtime: '23:30',
        wakeTime: '08:00',
        duration: '8.5h',
        quality: 72,
        consistency: 75,
        phases: [
          { type: 'light', duration: 35 },
          { type: 'deep', duration: 25 },
          { type: 'rem', duration: 25 },
          { type: 'awake', duration: 15 }
        ]
      },
      {
        date: '2025-10-10',
        bedtime: '22:00',
        wakeTime: '06:30',
        duration: '8.5h',
        quality: 88,
        consistency: 90,
        phases: [
          { type: 'light', duration: 22 },
          { type: 'deep', duration: 38 },
          { type: 'rem', duration: 32 },
          { type: 'awake', duration: 8 }
        ]
      },
      {
        date: '2025-10-11',
        bedtime: '22:30',
        wakeTime: '07:00',
        duration: '8.5h',
        quality: 85,
        consistency: 88,
        phases: [
          { type: 'light', duration: 25 },
          { type: 'deep', duration: 35 },
          { type: 'rem', duration: 30 },
          { type: 'awake', duration: 10 }
        ]
      }
    ];

    const mockEnvironmentData = {
      temperature: '68°F',
      noise: 'Low',
      screenTime: '45 min',
      caffeine: '2 cups',
      exerciseTime: '6 PM'
    };

    setSleepData(mockSleepData);
    setEnvironmentData(mockEnvironmentData);
  }, []);

  const handleLogSleep = (newSleepData) => {
    const sleepEntry = {
      date: new Date()?.toISOString()?.split('T')?.[0],
      bedtime: newSleepData?.bedtime,
      wakeTime: newSleepData?.wakeTime,
      duration: calculateDuration(newSleepData?.bedtime, newSleepData?.wakeTime),
      quality: newSleepData?.quality * 10, // Convert 1-10 to percentage
      consistency: 85,
      phases: [
        { type: 'light', duration: 25 },
        { type: 'deep', duration: 35 },
        { type: 'rem', duration: 30 },
        { type: 'awake', duration: 10 }
      ],
      notes: newSleepData?.notes
    };

    setSleepData(prev => [...prev, sleepEntry]);
    console.log('Sleep logged:', sleepEntry);
  };

  const calculateDuration = (bedtime, wakeTime) => {
    const bed = new Date(`2000-01-01T${bedtime}`);
    const wake = new Date(`2000-01-02T${wakeTime}`);
    const duration = (wake - bed) / (1000 * 60 * 60);
    return `${duration?.toFixed(1)}h`;
  };

  const getCurrentSleep = () => {
    const today = sleepData?.find(sleep => sleep?.date === selectedDate);
    return today ? today?.duration : '8.5h';
  };

  const getWeeklyAverage = () => {
    const recentSleep = sleepData?.slice(-7);
    const avgDuration = recentSleep?.reduce((sum, sleep) => 
      sum + parseFloat(sleep?.duration?.replace('h', '')), 0
    ) / recentSleep?.length;
    return `${avgDuration?.toFixed(1)}h`;
  };

  return (
    <>
      <Helmet>
        <title>Sleep Analysis - FormForge</title>
        <meta name="description" content="Comprehensive sleep pattern tracking and analysis for optimal wellness management" />
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Sleep Analysis</h1>
            <p style={{ color: '#6B7280' }}>Track your sleep patterns, quality, and trends for better wellness management</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <SleepTimeline sleepData={sleepData} selectedDate={selectedDate} onDateChange={setSelectedDate} />
              <SleepTrendChart sleepData={sleepData} />
              <div className="lg:hidden">
                <SleepInsights sleepData={sleepData} environmentData={environmentData} />
              </div>
            </div>

            <div className="space-y-6">
              <SleepGoalProgress currentSleep={getCurrentSleep()} sleepGoal="8.0h" weeklyAverage={getWeeklyAverage()} />
              <SleepLoggingControls onLogSleep={handleLogSleep} />
              <div className="hidden lg:block">
                <SleepInsights sleepData={sleepData} environmentData={environmentData} />
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { value: '7.2h', label: 'Average Sleep', change: '+0.3h from last week', color: '#00D4FF', positive: true },
              { value: '83%', label: 'Sleep Efficiency', change: '+5% from last week', color: '#AAFF00', positive: true },
              { value: '22:45', label: 'Avg Bedtime', change: '15min later than goal', color: '#FF6B00', positive: false },
              { value: '6', label: 'Consistent Days', change: 'Great consistency!', color: '#AAFF00', positive: true }
            ]?.map((stat) => (
              <div key={stat?.label} className="rounded-xl p-6 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
                <div className="text-2xl font-bold mb-2" style={{ fontFamily: "'Inter Tight', sans-serif", color: stat?.color }}>{stat?.value}</div>
                <div className="text-sm mb-1" style={{ color: '#6B7280' }}>{stat?.label}</div>
                <div className="text-xs" style={{ color: stat?.positive ? '#AAFF00' : '#FF6B00' }}>{stat?.change}</div>
              </div>
            ))}
          </div>
        </main>

        <QuickActionButton />
      </div>
    </>
  );
};

export default SleepAnalysis;